import React, { createContext, useContext, useEffect, useState } from "react";
import { en } from "./i18n/en";
import { getI18n } from "./i18n";
import { useAuth } from "./AuthContext";
import { PageSkeleton } from "../components/Loading";

type Translation = typeof en;

interface TranslationContextType extends Translation {
  setLanguage: (lang: string) => void;
}

const TranslationContext = createContext<TranslationContextType>({
  ...en,
  setLanguage: () => {},
});

export function TranslationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();
  const [language, setLanguage] = useState<string>(
    navigator.language.slice(0, 2) || "en"
  );

  useEffect(() => {
    if (auth.user?.language) {
      setLanguage(auth.user.language);
    }
  }, [auth.user?.language]);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const translations = getI18n(language);
  const contextValue = {
    ...translations,
    setLanguage: (lang: string) => setLanguage(lang),
  };

  return (
    <TranslationContext.Provider value={contextValue}>
      {auth.isLoading ? (
        <PageSkeleton
          className="p-4 pt-8"
          cards={3}
          ariaLabel={translations["Loading page content"]}
          loadingLabel={translations["Loading..."]}
        />
      ) : (
        children
      )}
    </TranslationContext.Provider>
  );
}

export function useTranslations() {
  return useContext(TranslationContext);
}
